import { useEffect, useState } from "react"

import { ContactResponses } from "@tfg/types/endpoints/contacts"

import {
  CreateBusinessPartnerRequestDto,
  organizationRequestDtoCountryEnum,
  OrganizationRequestDtoCountryEnumKey,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api"
import { useCreateContactMutation } from "~/pages/SupplierPortal/Contacts/hooks/useCreateContactMutation"
import { useUpdateBusinessPartnerMutation } from "~/pages/SupplierPortal/Contacts/hooks/useUpdateContactMutation"

type ContactRequestDto =
  | CreateBusinessPartnerRequestDto
  | (UpdateBusinessPartnerRequestDto & { id: string })
type UseContactFormProps = {
  initialValues?: ContactResponses.Contact
  onContactCreated?: (contactId: string) => void
}
export const contactToSupplierMapping = (
  contact: ContactResponses.Contact
): ContactRequestDto => {
  return {
    id: contact.id,
    supplierId: contact.user.id.toString(),
    name: contact.user.name,
    email: contact.user.email,
    preferedLangageIso6391: contact.user.preferedLangageIso6391,
    organization: {
      businessRelationship: contact.user.organization.businessRelationship,
      name: contact.user.organization.name,
      street: contact.user.organization.street ?? "",
      city: contact.user.organization.city ?? "",
      postalCode: contact.user.organization.postalCode ?? "",
      country: contact.user.organization
        .country as OrganizationRequestDtoCountryEnumKey,
      registrationId: contact.user.organization.registrationId,
    },
  }
}

const defaultContact: ContactRequestDto = {
  id: "",
  supplierId: "",
  name: "",
  email: "",
  preferedLangageIso6391: "EN",
  organization: {
    businessRelationship: {
      isActive: true,
      supplierCode: null,
      type: null,
    },
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: organizationRequestDtoCountryEnum.FR,
    registrationId: [],
  },
}

const useContactForm = ({
  initialValues,
  onContactCreated,
}: UseContactFormProps) => {
  const [contact, setContact] = useState<ContactRequestDto>(
    initialValues ? contactToSupplierMapping(initialValues) : defaultContact
  )
  const [supplierId, setSupplierId] = useState<string>("")

  const { mutate: createContactMutate, isPending: isCreating } =
    useCreateContactMutation({
      onSuccess: (data) => {
        onContactCreated?.(data)
        setContact((oldContact) => ({
          ...oldContact,
          supplierId: data.toString(),
        }))
      },
    })

  const createContact = (values: ContactRequestDto) => {
    createContactMutate(values)
  }

  const { mutate: updateContactMutate, isPending: isUpdating } =
    useUpdateBusinessPartnerMutation(supplierId)

  const updateContact = (values: UpdateBusinessPartnerRequestDto) => {
    updateContactMutate(values)
  }

  const handleContact = (values?: ContactRequestDto) => {
    if ("supplierId" in contact && contact.supplierId !== "") {
      updateContact((values as UpdateBusinessPartnerRequestDto) ?? contact)
    } else {
      createContact(values ?? contact)
    }
  }

  const reset = () => {
    setContact(defaultContact)
  }

  useEffect(() => {
    if ("supplierId" in contact && contact.supplierId !== "") {
      setSupplierId(contact.id.toString())
    }
  }, [contact])

  return {
    contact,
    setContact,
    supplierId,
    handleContact,
    reset,
    isCreating,
    isUpdating,
  }
}

export default useContactForm
